import type { Maybe } from '@adornis/base/utilTypes.js';
import { todayAsCalendarDay } from '@adornis/datetime/calendarDay.js';
import { BillingRhythm } from '../../billingPositions/entities/BillingPosition.js';
import { getMandatePhase, isFunded, MandatePhase, type MandatePhaseSubject } from '../api/MandatStatus.js';

/** Where the person's mandate list shows a mandate. */
export type MandateSection = 'pending' | 'active' | 'archive';

/**
 * Everything the UI needs to know about a mandate: its phase (`getMandatePhase`) mapped to
 * presentation and the actions allowed on it. It never controls billing.
 */
export interface MandateLifecycle {
  phase: MandatePhase;
  section: MandateSection;
  label: string | null;
  /** daisyUI badge class for the status */
  badgeClass: string;
  /** Border accent for the card */
  borderClass: string;
  /** Gefördert, befreit or pausiert: controlled by the funding, never edited on the card */
  funded: boolean;
  /**
   * A template whose group is in billing: it cannot be accepted until released - by the administrative SEPA roles
   * only a one-time one
   */
  acceptanceLocked: boolean;
  readonly: boolean;
  canAccept: boolean;
  canEnd: boolean;
  canSave: boolean;
}

type LifecycleSubject = MandatePhaseSubject & { amountInCents?: Maybe<number>; billingLocked?: Maybe<boolean> };

/**
 * `isAdministrator` are the administrative SEPA roles: they may correct an ended mandate, and the
 * group's billing lock does not bind them - for everybody else an accepted mandate of a group in
 * billing is read only, the server refuses the write the same way (`assertMandateNotInBilling`).
 *
 * An ended mandate is never brought back here: while its booking still needs a mandate, it is
 * offered again as the booking's template (`generateSepaMandatTemplates`), and accepting that
 * takes the row over.
 */
export function getMandateLifecycle(mandat: LifecycleSubject, isAdministrator = false): MandateLifecycle {
  const phase = getMandatePhase(mandat);
  const funded = isFunded(mandat);
  const over = phase === MandatePhase.ENDED || phase === MandatePhase.BILLED;
  // a one-time consent would collect a booking already billed as Selbstzahler, so not even the administration gives it
  const acceptanceLocked =
    phase === MandatePhase.TEMPLATE &&
    !!mandat.billingLocked &&
    (mandat.rhythm === BillingRhythm.ONCE_CUSTOM || !isAdministrator);
  const editLocked = !!mandat.billingLocked && !!mandat.accepted && !isAdministrator;

  const base = { phase, funded, acceptanceLocked, readonly: false, canAccept: false, canEnd: false, canSave: false };

  // a funded booking needs no consent, so even its template shows with the running mandates
  if (funded) {
    return {
      ...base,
      section: over ? 'archive' : 'active',
      label: over ? 'Vergangene Förderung' : null,
      badgeClass: over ? 'badge-neutral' : 'badge-primary',
      borderClass: over ? 'border-base-300' : 'border-primary',
      readonly: true,
    };
  }

  switch (phase) {
    case MandatePhase.TEMPLATE:
      return {
        ...base,
        section: 'pending',
        label: acceptanceLocked ? 'Zustimmung gesperrt' : 'Zustimmung ausstehend',
        badgeClass: acceptanceLocked ? 'badge-warning badge-outline' : 'badge-warning',
        borderClass: 'border-warning',
        canAccept: !acceptanceLocked,
      };
    case MandatePhase.PLANNED:
    case MandatePhase.RUNNING: {
      const endsToday = mandat.end?.getTime() === todayAsCalendarDay().getTime();
      const presentation =
        phase === MandatePhase.PLANNED
          ? { label: 'Geplant', badgeClass: 'badge-info', borderClass: 'border-info' }
          : endsToday
            ? // still running today - the active colour, outlined; warning is the template's
              { label: 'Endet heute', badgeClass: 'badge-success badge-outline', borderClass: 'border-success' }
            : { label: 'Aktiv', badgeClass: 'badge-success', borderClass: 'border-success' };
      return {
        ...base,
        ...presentation,
        section: 'active',
        readonly: editLocked,
        canSave: !editLocked,
        canEnd: !editLocked && !endsToday,
      };
    }
    case MandatePhase.ENDED:
      return {
        ...base,
        section: 'archive',
        label: 'Beendet',
        badgeClass: 'badge-neutral',
        borderClass: 'border-base-300',
        readonly: !isAdministrator,
        canSave: isAdministrator,
      };
    case MandatePhase.BILLED:
      return {
        ...base,
        section: 'archive',
        label: 'Abgerechnet',
        // the theme has no purple; literal palette tokens so Tailwind emits them
        badgeClass: 'border-violet-600 bg-violet-600 text-violet-50',
        borderClass: 'border-violet-600',
        readonly: true,
      };
  }
}
